import { useEffect, useState } from 'react';
import api from '../services/service';
import { toast } from 'react-toastify';
import { loginSuccess, logout, updateIsVerfied } from '../../redux/slices/authSlice';
import store from '../../redux/store';
import { resetWallet } from '../../redux/slices/walletSlice';
import { clearRememberMe, saveRememberMe } from '../remember';

export function useDebounce(value, delay, setPage) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    setPage(1);
    const handler = setTimeout(() => setDebounced(value), delay);

    return () => clearTimeout(handler); // cancel previous timeout
  }, [value, delay, setPage]);

  return debounced;
}

let toastId = null;
export const showToast = (message) => {
  if (!toast.isActive(toastId)) {
    toastId = toast(message);
  }
};

export const errorResponseMessage = (error) => {
  const response = error?.response;
  if (error?.code === 'ERR_NETWORK') {
    showToast('Network Error');
  } else if (response?.status === 500) {
    showToast(response?.statusText || response?.data?.message);
  } else if (response?.status === 400) {
   if (
  (typeof response?.data?.data === 'object' && response?.data?.data !== null) ||
  !('message' in (response?.data || {}))
) {
      Object.entries(response?.data?.data || response?.data).forEach(([key, value]) => {
        toast(
          `${key
            .split('_')
            .reduce(
              (acc, word) =>
                acc + word.charAt(0).toUpperCase() + word.slice(1) + ' ',
              ''
            )
            .trim()}: ${value}`
        );
      });
    }else{
      const message = response?.data?.message;
      if (Array.isArray(message)) {
        message.forEach(msg => showToast(msg));
      } else if (message) {
        showToast(message);
      }
    }
  } else {
    showToast(response?.data?.message);
  }
};

export const getListData = async (
  endpoint,
  setLoading,
  setData,
  page = 1,
  page_size = 10,
  params = ''
) => {
  await api
    .get(`${endpoint}?page=${page}&page_size=${page_size}${params}`)
    .then(function (response) {
      setData(response?.data);
    })
    .catch(function (error) {
      // handle error
      console.log(error);
      errorResponseMessage(error);
    })
    .finally(function () {
      // always executed
      setLoading(false);
    });
};

export const createData = async (
  endpoint,
  setLoading,
  body,
  navigate,
  redirect
) => {
  const headers = {};
  if (body instanceof FormData) {
    headers['Content-Type'] = 'multipart/form-data';
  }

  api
    .post(endpoint, body, { headers })
    .then(function (response) {
      // handle success
      if (navigate) navigate(redirect);
      showToast(response.data.message);
    })
    .catch(function (error) {
      // handle error
      errorResponseMessage(error);
    })
    .finally(function () {
      setLoading(false);
    });
};
export const postData = async (
  endpoint,
  setLoading,
  body,
  setData
) => {
  const headers = {};
  if (body instanceof FormData) {
    headers['Content-Type'] = 'multipart/form-data';
  }

  api
    .post(endpoint, body, { headers })
    .then(function (response) {
      // handle success
      // showToast(response.data.message);
    })
    .catch(function (error) {
      // handle error
      errorResponseMessage(error);
    })
    .finally(function () {
      setLoading(false);
    });
};

export const getData = async (endpoint, setLoading, setData, id) => {
  await api
    .get(`${endpoint}${id}/`)
    .then(function (response) {
      setData(response?.data?.data);
    })
    .catch(function (error) {
      // handle error
      console.log(error);
      errorResponseMessage(error);
    })
    .finally(function () {
      setLoading(false);
    });
};

export const updateData = async (
  endpoint,
  setLoading,
  body,
  navigate,
  redirect
) => {
  const headers = {};
  if (body instanceof FormData) {
    headers['Content-Type'] = 'multipart/form-data';
  }

  await api
    .put(endpoint, body, { headers })
    .then(function (response) {
      if (endpoint === "/users/update-profile/") store.dispatch(updateIsVerfied(response.data?.data))
      if (navigate) navigate(redirect);
      showToast(response?.data?.message);
    })
    .catch(function (error) {
      // handle error
      console.log(error);
      errorResponseMessage(error);
    })
    .finally(function () {
      // always executed
      setLoading(false);
    });
};

export const deleteData = async (endpoint, id) => {
  await api
    .delete(`${endpoint}${id}/`)
    .then(function (response) {
      showToast(response?.data?.message);
    })
    .catch(function (error) {
      // handle error
      console.log(error);
      errorResponseMessage(error);
    });
};


export const getList = async (
  endpoint,
  setLoading,
  setData
) => {
  await api
    .get(`${endpoint}`)
    .then(function (response) {
      setData(response?.data?.data);
    })
    .catch(function (error) {
      // handle error
      console.log(error);
      errorResponseMessage(error);
    })
    .finally(function () {
      // always executed
      if (setLoading) setLoading(false);
    });
};

export const patchData = async (
  endpoint,
  payload,
  setLoading,
) => {
  if (setLoading) setLoading(true);
  try {
    const response = await api.patch(`${endpoint}`, payload);
    return response?.data; 
  } catch (error) {
    console.log(error);
    errorResponseMessage(error);
    return null;
  } finally {
    if (setLoading) setLoading(false);
  }
};


export const userSignUp = async (body, setLoading, navigate, redirect) => {
  await api
    .post('/users/signup/', body)
    .then((response) => {
      const { tokens, user } = response.data;
      store.dispatch(loginSuccess({ token: tokens, user }));
      sessionStorage.setItem("otpStarted", "true");
      navigate(redirect);
      showToast(response.data.message);
    })
    .catch((error) => {
      errorResponseMessage(error);
    })
    .finally(() => {
      setLoading(false);
    });
};

export const userLogin = async (body, setLoading, navigate, redirect, remember) => {
  await api
    .post('/users/login/', body)
    .then((response) => {
      const { tokens, user } = response.data;

      // Dispatch to Redux (sessionStorage)
      const authData = { token: tokens, user };
      store.dispatch(loginSuccess(authData));

      // If remember checked → also backup to localStorage
      if (remember) {
        saveRememberMe(authData);
      } else {
        clearRememberMe();
      }

      navigate(redirect);
      showToast(response.data.message);
    })
    .catch((error) => {
      errorResponseMessage(error);
    })
    .finally(() => {
      setLoading(false);
    });
};


export const googleLogin = async (token, user_type, setLoading, navigate, redirect, remember, referral_code = null , uid = null) => {
  await api
    .post('/users/auth/google/', { token, user_type, referral_code, uid })
    .then((response) => {
      const { tokens, user, message } = response.data;
      
      // Dispatch to Redux (sessionStorage)
      const authData = { token: tokens, user };
      store.dispatch(loginSuccess(authData));

      // If remember checked → also backup to localStorage
      if (remember) {
        saveRememberMe(authData);
      } else {
        clearRememberMe();
      }

      navigate(redirect);
      showToast(message);
    })
    .catch((error) => {
      errorResponseMessage(error);
    })
    .finally(() => {
      setLoading(false);
    });
};


export const userLogout = async (navigate, redirect) => {
  const refreshToken = store.getState().auth.token?.refresh;

  if (!refreshToken) {
    store.dispatch(logout());
    navigate(redirect);
    store.dispatch(resetWallet());
    clearRememberMe();
    return;
  }

  try {
    const response = await api.post('/users/logout/', { refresh: refreshToken });
    store.dispatch(logout());
    store.dispatch(resetWallet());
    clearRememberMe();
    navigate(redirect);
    showToast(response?.data?.message || "Logout successful.");
  } catch (error) {
 
    store.dispatch(logout());
    store.dispatch(resetWallet());
    clearRememberMe();
    navigate(redirect);
    showToast("Logged out locally due to server error.");
  }
};

export const otpVerify = async (endpoint, body, setLoading, setSuccess, setClose) => {
  await api
    .post(endpoint, body)
    .then((response) => {
      setClose(false)
      setSuccess(true)
      setTimeout(()=>{
        store.dispatch(updateIsVerfied(response.data?.data))
        setSuccess(false)
      },3000)
    })
    .catch((error) => {
      errorResponseMessage(error);
    })
    .finally(() => {
      setLoading(false);
    });
};

export const completeRegistration = async (endpoint, body, setLoading) => {
  await api
    .put(endpoint, body)
    .then((response) => {
      showToast(response.data.message);
      store.dispatch(updateIsVerfied(response.data?.data))
    })
    .catch((error) => {
      errorResponseMessage(error);
    })
    .finally(() => {
      setLoading(false);
    });
};

export const getCall = async (
  endpoint,
  setLoading
) => {
  await api
    .get(`${endpoint}`)
    .then(function (response) {
      showToast(response?.data?.message)
    })
    .catch(function (error) {
      // handle error
      console.log(error);
      errorResponseMessage(error);
    })
    .finally(function () {
      // always executed
      if (setLoading) setLoading(false);
    });
};

// ResetPassword
export const createResetData = async (
  endpoint,
  setLoading,
  body,
  onSuccess = () => {},
  redirect = "",
  navigate = null // <- Add this
) => {
  try {
    const response = await api.post(endpoint, body);
    showToast(response.data.message);
    onSuccess();
    if (redirect && navigate) {
      navigate(redirect);
    }
  } catch (error) {
    errorResponseMessage(error);
  } finally {
    setLoading(false);
  }
};

export const createPaymentData = async (endpoint, setLoading, body) => {
  const headers = {};
  if (body instanceof FormData) {
    headers['Content-Type'] = 'multipart/form-data';
  }

  try {
    const response = await api.post(endpoint, body, { headers });
    showToast(response.data.message);
    return response; // let caller handle response
  } catch (error) {
    errorResponseMessage(error);
    throw error; // let caller handle failure
  } finally {
    setLoading(false);
  }
};

export const gethWalletBalance = async () => {
  try {
    const response = await api.get("/orders/wallet/");
    return response.data?.data || 0;
  } catch (error) {
    console.error("Error fetching wallet balance:", error);
    throw error;
  }
};

export const checkOtpExpired = async (body) => {
  try {
    const response = await api.post("/users/otp-expired/", body);
    return response.data || {};
  } catch (error) {
    return error.response.data || {}
  }
};